"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from "react";
import { IconCircleCheckOutline18, IconWalletOutline18 } from "nucleo-ui-outline-18";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Button } from "@/components/ui/button";
import { GlassIcon } from "@/components/ui/glass-icon";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { cn, formatAddress } from "@/lib/utils";
import { ConnectorLogo } from "@/app/(v2)/connectors/_components/ConnectorLogo";
import { POLYMARKET_ICON_URL } from "@/lib/polymarket-constants";
import { EASE_OUT_QUART, MOTION_DURATION } from "@/lib/animation-constants";
import { V2_ENDPOINT } from "@/consts";
import { ApiError, authenticatedFetchJson } from "@/lib/authenticatedFetch";
import { useOrganizations } from "@/hooks/useOrganizations";
import type { OrganizationWallet } from "@/types/wallet";
import { PolymarketFundingContent } from "./PolymarketFundingContent";
interface PolymarketDepositWalletSetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wallets: OrganizationWallet[];
  onSetupCompleted: (funded: boolean) => void;
}
type WizardStep = "setup" | "created" | "fund";
const TOTAL_STEPS = 3;
interface SetupResponse {
  code: number;
  data: {
    walletId: string;
    depositWalletAddress: string;
  };
}
export function PolymarketDepositWalletSetupModal({
  open,
  onOpenChange,
  wallets,
  onSetupCompleted
}: PolymarketDepositWalletSetupModalProps) {
  const [step, setStep] = useState<WizardStep>("setup");
  const [depositWalletAddress, setDepositWalletAddress] = useState<string | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const {
    selectedOrgId
  } = useOrganizations();
  const shouldReduceMotion = useReducedMotion();
  const stepVariants = useMemo(() => ({
    enter: shouldReduceMotion ? {
      opacity: 1,
      x: 0
    } : {
      opacity: 0,
      x: 18
    },
    center: {
      opacity: 1,
      x: 0,
      transition: shouldReduceMotion ? {
        duration: 0
      } : {
        duration: MOTION_DURATION.normal,
        ease: EASE_OUT_QUART
      }
    },
    exit: shouldReduceMotion ? {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0
      }
    } : {
      opacity: 0,
      x: -18,
      transition: {
        duration: MOTION_DURATION.fast,
        ease: EASE_OUT_QUART
      }
    }
  }), [shouldReduceMotion]);
  const handleDeploy = useCallback(async () => {
    if (!selectedOrgId) return;
    setIsDeploying(true);
    setDeployError(null);
    try {
      const response = await authenticatedFetchJson<SetupResponse>(`${V2_ENDPOINT}/v1/organizations/${selectedOrgId}/wallets/polymarket-deposit`, {
        method: "POST",
        orgId: selectedOrgId
      });
      const address = response.data.depositWalletAddress;
      setDepositWalletAddress(address);
      setStep("created");
    } catch (e) {
      let msg = "Failed to create deposit wallet. Please try again.";
      if (e instanceof ApiError && e.details.length > 0) {
        const detail = e.details[0];
        if (typeof detail === "string") msg = detail;else if (typeof detail === "object" && detail !== null && "message" in detail) msg = String(detail.message);
      } else if (e instanceof TypeError) {
        msg = "Polymarket's servers are temporarily unavailable. Please try again.";
      } else if (e instanceof Error && e.message !== "internal server error") {
        msg = e.message;
      }
      setDeployError(msg);
    } finally {
      setIsDeploying(false);
    }
  }, [selectedOrgId]);
  const handleFundingCompleted = useCallback(() => {
    onSetupCompleted(true);
    onOpenChange(false);
  }, [onSetupCompleted, onOpenChange]);
  const handleSkipFunding = useCallback(() => {
    onSetupCompleted(false);
    onOpenChange(false);
  }, [onSetupCompleted, onOpenChange]);
  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen && isDeploying) return;
    if (!nextOpen) {
      if (step === "created" || step === "fund") {
        onSetupCompleted(false);
      }
      setStep("setup");
      setDepositWalletAddress(null);
      setDeployError(null);
      setIsDeploying(false);
    }
    onOpenChange(nextOpen);
  }, [isDeploying, onOpenChange, step, onSetupCompleted]);
  const currentStepNumber = step === "setup" ? 1 : step === "created" ? 2 : 3;
  return <ResponsiveModal open={open} onOpenChange={handleOpenChange} title="Set up Polymarket Wallet" contentClass="max-w-md z-[10001]" innerClass="p-0" overlayClassName="z-[10000]" preventCloseOnOutsideClick={isDeploying}>
      <div className="flex min-w-0 flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-5 pb-3 pt-5">
          <GlassIcon icon={IconWalletOutline18} color="blue" size="md" />
          <div className="min-w-0 flex-1">
            <h2 className="body-semibold leading-tight">Set up Polymarket Wallet</h2>
            <p className="label-xs text-ds-text-muted mt-0.5">Dedicated trading wallet for Polymarket</p>
          </div>
        </div>

        <div className="flex justify-center gap-1.5 pb-3">
          {Array.from({
          length: TOTAL_STEPS
        }, (_, i) => <div key={i} className={cn("h-1.5 rounded-full transition-all duration-300", i < currentStepNumber ? "bg-ds-brand-500 w-6" : "bg-ds-neutral-300 dark:bg-ds-neutral-700 w-1.5")} />)}
        </div>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={step} variants={stepVariants} initial="enter" animate="center" exit="exit" className="px-5 pb-5">
            {step === "setup" && <SetupStep isDeploying={isDeploying} error={deployError} onDeploy={handleDeploy} />}
            {step === "created" && depositWalletAddress && <CreatedStep address={depositWalletAddress} onContinue={() => setStep("fund")} onSkip={handleSkipFunding} />}
            {step === "fund" && depositWalletAddress && <div className="flex flex-col gap-4">
                <PolymarketFundingContent recipientAddress={depositWalletAddress} wallets={wallets} onFundingCompleted={handleFundingCompleted} onBeforeOpenExternal={handleSkipFunding} />
                <div className="flex justify-center">
                  <Button variant="ghost" size="sm" onClick={handleSkipFunding}>
                    I&apos;ll fund later
                  </Button>
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </ResponsiveModal>;
}
function SetupStep(t0) {
  const $ = _c(13);
  const {
    isDeploying,
    error,
    onDeploy
  } = t0;
  if (isDeploying) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex flex-col items-center gap-3 py-8"><PixelLoader size="md" /><p className="body-semibold">Deploying wallet...</p><p className="label-xs text-ds-text-muted text-center">This usually takes 10-15 seconds.</p></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex justify-center py-2"><ConnectorLogo type="polymarket" name="Polymarket" logoUrl={POLYMARKET_ICON_URL} size="lg" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== error) {
    t2 = error && <div className="rounded-lg bg-red-50 px-3 py-2 dark:bg-red-950/30"><p className="label-xs text-red-600 dark:text-red-400">{error}</p></div>;
    $[2] = error;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="body-sm text-ds-text-primary">Polymarket requires a dedicated wallet to sign and place orders. This creates a separate wallet on Polygon that holds pUSD for trading.</p>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col gap-2">{t3}<ul className="label-xs text-ds-text-muted list-disc space-y-1 pl-4"><li>Only used for Polymarket — your main wallet stays untouched</li><li>Signs orders directly on Polymarket's CLOB</li><li>Fund with any token from any chain — auto-bridged to pUSD</li></ul></div>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = error ? "white" : "brand";
  const t6 = error ? "Retry" : "Set up wallet";
  let t7;
  if ($[6] !== onDeploy || $[7] !== t5 || $[8] !== t6) {
    t7 = <Button variant={t5} onClick={onDeploy} className="w-full">{t6}</Button>;
    $[6] = onDeploy;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t2 || $[11] !== t7) {
    t8 = <div className="flex flex-col gap-4">{t1}{t2}{t4}{t7}</div>;
    $[10] = t2;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
function CreatedStep(t0) {
  const $ = _c(16);
  const {
    address,
    onContinue,
    onSkip
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ConnectorLogo type="polymarket" name="Polymarket" logoUrl={POLYMARKET_ICON_URL} size="lg" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="relative">{t1}<div className="bg-ds-surface-primary absolute -bottom-1 -right-1 rounded-full p-0.5"><IconCircleCheckOutline18 className="text-ds-green-600 h-5 w-5" /></div></div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col items-center gap-3 py-2">{t2}<div className="flex flex-col items-center gap-1"><p className="body-semibold">Wallet created</p><p className="label-xs text-ds-text-muted text-center">Your Polymarket deposit wallet is ready on Polygon. Fund it to start trading.</p></div></div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-2"><IconWalletOutline18 className="text-ds-text-muted h-4 w-4 shrink-0" /><span className="label-xs text-ds-text-muted">Deposit Wallet</span></div>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== address) {
    t5 = formatAddress(address, 6, 4);
    $[4] = address;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <div className="border-ds-border-default flex items-center justify-between rounded-lg border px-3 py-2.5">{t4}<span className="mono-xs text-ds-text-primary">{t5}</span></div>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== onContinue) {
    t7 = <Button variant="brand" onClick={onContinue} className="w-full">Fund wallet</Button>;
    $[8] = onContinue;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== onSkip) {
    t8 = <div className="flex justify-center"><Button variant="ghost" size="sm" onClick={onSkip}>I'll fund later</Button></div>;
    $[10] = onSkip;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = <div className="flex flex-col gap-4">{t3}{t6}{t7}{t8}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}